import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAuthToken } from "../../redux/actions/userActions";
import { APIConstants, LoginConstants } from "../../utilities/Constants";
const TOKEN_REFRESH_INTERVAL = 20 * 60 * 1000;
const INACTIVITY_TIMEOUT = 20 * 60 * 1000;

export default function useAuthAndInactivity(userData, userPracticeID, userId) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tokenIntervalRef = useRef(null);
  const inactivityIntervalRef = useRef(null);
  const initializedRef = useRef(false);

  const logout = useCallback(async () => {
    if (!userData) return;

    try {
      const inputJson = { UserID: userId, PracticeID: userPracticeID };
      const url = `${APIConstants.SERVICES_URL}${LoginConstants.Logout}?sInputJSON=${JSON.stringify(inputJson)}`;

      const res = await fetch(url, { method: "POST" });
      // if (res.ok) {
        localStorage.clear();
        sessionStorage.clear();
        dispatch({ type: "SET_USER", payload: null });
        dispatch({ type: "SET_AUTHTOKEN", payload: null });
        navigate("/");
      // }
    } catch (err) {
      console.error("Logout error:", err);
    }
  }, [userData, userPracticeID, userId, dispatch, navigate]);

  const fetchNewToken = useCallback(async () => {
    if (!userData) return;

    let attempts = 0;
    const maxRetries = 3;

    const tryFetch = async () => {
      try {
        const inputJSON = { UserName: userData.Username, PracticeId: userPracticeID };
        const url = `${APIConstants.SERVICES_URL}${LoginConstants.UpdatedToken}?sInputJSON=${JSON.stringify(inputJSON)}`;

        const res = await fetch(url);
        if (!res.ok) throw new Error("Token fetch failed");

        const parsedData = await res.json();
        dispatch(setAuthToken(parsedData.AuthToken));
        localStorage.setItem("sAuthToken", parsedData.AuthToken);
        return true;
      } catch (err) {
        attempts++;
        console.error(`Token refresh failed (attempt ${attempts})`, err);
        if (attempts < maxRetries) {
          const waitTime = Math.pow(2, attempts) * 5000;
          setTimeout(tryFetch, waitTime);
        } else {
          logout();
        }
      }
    };

    tryFetch();
  }, [userData, userPracticeID, dispatch, logout]);

  useEffect(() => {
    if (!userData || initializedRef.current) return;
    initializedRef.current = true;

    // ✅ Refresh token every 20 mins
    tokenIntervalRef.current = setInterval(fetchNewToken, TOKEN_REFRESH_INTERVAL);

    const events = ["mousemove", "keypress", "click"];
    const resetTimer = () => localStorage.setItem("lastActivity", Date.now().toString());
    const checkInactivity = () => {
      const lastActivity = parseInt(localStorage.getItem("lastActivity") || "0", 10);
      if (Date.now() - lastActivity > INACTIVITY_TIMEOUT) {
        console.log("Logging out due to inactivity");
        cleanup();
        logout();
      }
    };
    const cleanup = () => {
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
      clearInterval(tokenIntervalRef.current);
      clearInterval(inactivityIntervalRef.current);
      tokenIntervalRef.current = null;
      inactivityIntervalRef.current = null;
      initializedRef.current = false;
    };

    events.forEach(ev => window.addEventListener(ev, resetTimer));
    inactivityIntervalRef.current = setInterval(checkInactivity, 1000);
    resetTimer();

    return cleanup;
  }, [userData, fetchNewToken, logout]);

  return { logout };
}